import { api, LightningElement } from "lwc";

import { Listbox, ListboxState } from "./lib/listbox";

import { Completion, RecentSection, SearchEvent } from "./types";

export default class extends LightningElement {
  @api completions: Completion[] | null = [];
  @api defaultValue: string | null = null;
  @api placeholder: string = "Search";
  @api recents: RecentSection[] | null = null;

  private completionTerms: string | null = null;
  private setup = false;

  private listbox = new Listbox({
    onChange: (state) => {
      this.listboxState = state;
    },
    onSelect: (index) => {
      let { completions, hasCompletions, hasRecents, recents } = this;
      if (hasCompletions) {
        this.dispatchSearchEvent({
          type: "completion",
          value: completions![index],
        });
      } else if (hasRecents) {
        this.dispatchSearchEvent({
          type: "recent",
          value: recents!.flatMap((s) => s.items)[index],
        });
      }
    },
    onSubmit: () => {
      let { completionTerms } = this;
      if (!completionTerms) return;
      this.dispatchSearchEvent({ type: "input", value: completionTerms });
    },
    shadowRoot: this.template,
  });
  private listboxState: ListboxState | null = null;

  private get hasCompletions() {
    return this.completions && this.completions.length > 0;
  }

  private get hasRecents() {
    return this.recents && this.recents.length > 0;
  }

  private get recentsWithStyle() {
    return this.recents!.map((section) =>
      Object.assign({}, section, {
        items: section.items.map((item) =>
          Object.assign({}, item, {
            iconStyle: item.iconBackgroundColor
              ? `background-color: ${item.iconBackgroundColor};`
              : "",
          })
        ),
      })
    );
  }

  private dispatchSearchEvent(event: SearchEvent) {
    this.dispatchEvent(new CustomEvent("search", { detail: event }));
  }

  private onInput(e: InputEvent) {
    let { value } = e.currentTarget as HTMLInputElement;
    let terms = value.trim();
    this.completionTerms = terms;
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: { value: terms },
      })
    );
  }

  private get showRecents() {
    let { completionTerms, hasCompletions, hasRecents, listboxState } = this;
    if (!listboxState) return false;
    if (completionTerms && completionTerms.length) return false;
    return listboxState.active && hasRecents && !hasCompletions;
  }

  private get showCompletions() {
    let { hasCompletions, listboxState } = this;
    if (!listboxState) return false;
    return listboxState.active && hasCompletions;
  }

  renderedCallback() {
    if (!this.setup) {
      this.setup = true;
      if (this.defaultValue) {
        let input = this.template.querySelector("input") as HTMLInputElement;
        input.value = this.defaultValue;
        this.completionTerms = this.defaultValue;
      }
    }
    this.listbox.renderedCallback();
  }

  disconnectedCallback() {
    this.listbox.disconnectedCallback();
  }
}
